import { uniq } from 'es-toolkit';
import React, { FunctionComponent, useState } from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';

import { PropertyTextInput, withoutZeroEntries } from '@paragon/client-tools';
import { DEFAULT_ORGANIZATION_SETTINGS, DistanceFormat } from '@paragon/contract-library';
import { enqueueError } from '@paragon/notification-tools';
import { closeCurrentPopup, Dialog } from '@paragon/popup-state';
import { naturalSort } from '@paragon/utilities';

import { downloadShopDrawings } from '../../store/actions/download';
import { getScopeProjectGuids, ProjectActionScope } from '../../store/projectActionScope';
import { useAppDispatch, useAppSelector } from '../hooks';
import { ChooseQuantities, QuantityOption, useQuantitiesAndOptionState } from './ChooseQuantities';

type CutSheetConfiguration = 'combined' | 'underneath' | 'separate';

export const ShopDrawingOptionsDialog: FunctionComponent<{ readonly scope: ProjectActionScope }> = ({ scope }) => {
  const dispatch = useAppDispatch();

  const organizationSettings = useAppSelector((state) => state.application.organizationSettings) ?? {
    ...DEFAULT_ORGANIZATION_SETTINGS,
    organization: '',
  };
  const concatenatedProjectNames = useAppSelector((state) => {
    const projectGuids = getScopeProjectGuids(scope);
    const projects = Object.values(state.layoutModels).filter((model) => projectGuids.includes(model.guid));
    return uniq(projects.map((project) => project.name))
      .sort(naturalSort)
      .join(' ');
  });

  const [name, setName] = useState(concatenatedProjectNames);
  const [includeComponentSchematics, setIncludeComponentSchematics] = useState(true);
  const [includeCutSheets, setIncludeCutSheets] = useState(true);
  const [isPortraitOrientation, setIsPortraitOrientation] = useState(
    organizationSettings.portraitOrientationShopDrawings,
  );
  const [cutSheetConfiguration, setCutSheetConfiguration] = useState<CutSheetConfiguration>(
    organizationSettings.portraitOrientationShopDrawings ? 'underneath' : 'separate',
  );
  const [distanceFormat, setDistanceFormat] = useState(organizationSettings.useDistanceFormat);
  const [squareCutAngleIsZero, setSquareCutAngleIsZero] = useState(organizationSettings.squareCutAngleIsZero);

  const [quantities, setQuantities, quantityOption, setQuantityOption] = useQuantitiesAndOptionState(scope);

  const onQuantityChange = (quantityOption: QuantityOption, quantities: { [guid: string]: number }) => {
    setQuantityOption(quantityOption);
    setQuantities(quantities);
  };
  return (
    <Dialog
      title="Shop Drawing Options"
      onCancel="close"
      submitText="Download"
      onSubmit={() => {
        if (!includeComponentSchematics && !includeCutSheets) {
          dispatch(enqueueError('Select at least one item in "What to include".'));
          return;
        }
        dispatch(
          downloadShopDrawings(
            withoutZeroEntries(quantities),
            {
              includeComponentSchematics,
              includeCutSheets,
              combineCutSheets: cutSheetConfiguration === 'combined',
              cutsOnSamePage: cutSheetConfiguration === 'underneath',
              portraitOrientation: isPortraitOrientation,
              distanceFormat,
              squareCutAngleIsZero,
              includedMemberTypes: ['Chord', 'Web', 'Block', 'Other'],
            },
            name,
          ),
        );
        dispatch(closeCurrentPopup());
      }}
    >
      <PropertyTextInput property="Group Name" value={name} submit={setName} fullWidth />
      <Grid container spacing={1}>
        <Grid size={6}>
          <Typography variant="subtitle1">What to Include</Typography>
          <FormGroup sx={{ display: 'flex', flexDirection: 'column', mb: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeComponentSchematics}
                  onChange={(_, checked) => {
                    setIncludeComponentSchematics(checked);

                    if (!checked && cutSheetConfiguration === 'underneath') setCutSheetConfiguration('separate');
                    if (checked && includeCutSheets && isPortraitOrientation && cutSheetConfiguration !== 'combined')
                      setCutSheetConfiguration('underneath');
                  }}
                />
              }
              label="Include component schematics"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeCutSheets}
                  onChange={(_, checked) => {
                    setIncludeCutSheets(checked);

                    if (!checked) setCutSheetConfiguration('separate');
                    if (checked && includeComponentSchematics && isPortraitOrientation)
                      setCutSheetConfiguration('underneath');
                  }}
                />
              }
              label="Include cut sheets"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={isPortraitOrientation}
                  onChange={(_, checked) => {
                    setIsPortraitOrientation(checked);

                    if (!checked && cutSheetConfiguration === 'underneath') setCutSheetConfiguration('separate');
                    if (
                      checked &&
                      includeComponentSchematics &&
                      includeCutSheets &&
                      cutSheetConfiguration !== 'combined'
                    )
                      setCutSheetConfiguration('underneath');
                  }}
                />
              }
              label="Portrait orientation"
            />
            {includeCutSheets ? (
              <FormControl sx={{ m: 1, flexBasis: '100%' }}>
                <InputLabel>Cut Sheet Configuration</InputLabel>
                <Select
                  variant="outlined"
                  fullWidth
                  value={cutSheetConfiguration}
                  onChange={(event) => setCutSheetConfiguration(event.target.value as CutSheetConfiguration)}
                  label="Cut Sheet Configuration"
                >
                  <MenuItem value="combined">Combined cut sheets</MenuItem>
                  {isPortraitOrientation && includeComponentSchematics ? (
                    <MenuItem value="underneath">Cuts underneath schematics</MenuItem>
                  ) : null}
                  <MenuItem value="separate">Separate cut sheets</MenuItem>
                </Select>
              </FormControl>
            ) : null}
          </FormGroup>
          <FormGroup sx={{ display: 'flex', flexDirection: 'column', mb: 3 }}>
            <Typography variant="subtitle1">Format Settings</Typography>
            <Box sx={{ display: 'flex', flex: 1 }}>
              <FormControl sx={{ m: 1, flexBasis: '100%' }}>
                <InputLabel>Distance Format</InputLabel>
                <Select
                  variant="outlined"
                  fullWidth
                  value={distanceFormat}
                  onChange={(event) => setDistanceFormat(event.target.value as DistanceFormat)}
                  label="Distance Format"
                >
                  <MenuItem value="FeetInchesSixteenths">Feet-Inches-Sixteenths</MenuItem>
                  <MenuItem value="Architectural">Architectural</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ m: 1, flexBasis: '100%' }}>
                <InputLabel>Square Cut Angle</InputLabel>
                <Select
                  variant="outlined"
                  fullWidth
                  value={squareCutAngleIsZero ? '0' : '90'}
                  onChange={(event) => setSquareCutAngleIsZero(event.target.value === '0')}
                  label="Square Cut Angle"
                >
                  <MenuItem value="0">0 degrees</MenuItem>
                  <MenuItem value="90">90 degrees</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </FormGroup>
        </Grid>
        <Grid size={6}>
          <Typography variant="subtitle1">Quantities</Typography>
          <ChooseQuantities
            scope={scope}
            quantityOption={quantityOption}
            quantities={quantities}
            onQuantityChange={onQuantityChange}
          />
        </Grid>
      </Grid>
    </Dialog>
  );
};
